import React from "react";
import FrameButton from "../buttons/FrameButton";
import { MdCheck } from "react-icons/md";
import classNames from "classnames";
import { isDesktop } from "react-device-detect";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";

interface ClaimBadgeButtonProps {
    // Context about what we're claiming
    type: "quest" | "milestone";

    // IDs for claiming
    questId: string;
    milestoneId?: string;

    // State data
    isComplete: boolean;
    claimedAt?: Date | number;
    hasUnclaimedMilestones?: boolean;
    isCollapsed?: boolean;
    hasRootLevelRewards?: boolean;
    allMilestonesClaimed?: boolean;

    // Actions
    onClaim: (id: string) => Promise<void>;
    onUncollapseAndScroll?: (e: React.MouseEvent<HTMLAnchorElement>) => void;

    // UI state
    isClaimingInProgress: boolean;
    windowWidth: number;
    forceModalDesign?: boolean;

    // Style
    className?: string;
}

const MOBILE_BREAKPOINT = 576;
const MEDIUM_BREAKPOINT = 1280;
const LARGE_BREAKPOINT = 1650;

export default function ClaimBadgeButton({
    type,
    questId,
    milestoneId,
    isComplete,
    claimedAt,
    hasUnclaimedMilestones,
    isCollapsed,
    hasRootLevelRewards,
    allMilestonesClaimed,
    onClaim,
    onUncollapseAndScroll,
    isClaimingInProgress,
    windowWidth,
    forceModalDesign,
    className,
}: ClaimBadgeButtonProps) {
    const { isOnOverwolf } = useOverwolfInterop();
    const isMobileView = windowWidth <= MOBILE_BREAKPOINT || forceModalDesign;
    const isMediumBreakpoint = windowWidth >= MEDIUM_BREAKPOINT && windowWidth < LARGE_BREAKPOINT;

    // Determine what to render based on state
    const determineRenderState = (): "claim" | "claimed" | "all-claimed" | "uncollapse" | "none" => {
        // If already claimed, show claimed badge
        if (claimedAt) {
            return "claimed";
        }

        // If this is a quest-level button
        if (type === "quest") {
            // If there are unclaimed milestones and we're collapsed, show uncollapse button
            if (hasUnclaimedMilestones && isCollapsed) {
                return "uncollapse";
            }
            // If there are unclaimed milestones and we're not collapsed, don't show anything
            if (hasUnclaimedMilestones && !isCollapsed) {
                return "none";
            }
            // If quest is complete and no unclaimed milestones and has root rewards, show claim
            if (isComplete && !hasUnclaimedMilestones && hasRootLevelRewards) {
                return "claim";
            }
            // If all milestones are claimed and no root level rewards, show "All Claimed"
            if (allMilestonesClaimed && !hasRootLevelRewards && !hasUnclaimedMilestones) {
                return "all-claimed";
            }
            return "none";
        }

        // For milestone-level buttons
        if (type === "milestone") {
            // If milestone is complete, show claim
            if (isComplete) {
                return "claim";
            }
            return "none";
        }

        return "none";
    };

    const renderState = determineRenderState();

    if (renderState === "none") {
        return null;
    }

    // Render claimed badge
    if (renderState === "claimed") {
        const claimedDate = new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
        }).format(claimedAt);

        return (
            <FrameButton
                size="narrow"
                className={classNames(
                    "go-vip-ow_quest-reward-card_claimed",
                    {
                        "desktop-claimed-btn": !isMobileView && !isMediumBreakpoint,
                        "mobile-claimed-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                Claimed on {claimedDate} {(isOnOverwolf || isDesktop) && <MdCheck />}
            </FrameButton>
        );
    }

    // Render "All Claimed" badge
    if (renderState === "all-claimed") {
        return (
            <FrameButton
                size="narrow"
                className={classNames(
                    "go-vip-ow_quest-reward-card_claimed",
                    {
                        "desktop-claimed-btn": !isMobileView && !isMediumBreakpoint,
                        "mobile-claimed-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                All Claimed {(isOnOverwolf || isDesktop) && <MdCheck />}
            </FrameButton>
        );
    }

    // Render uncollapse button (for quest-level when milestones need claiming)
    if (renderState === "uncollapse") {
        return (
            <FrameButton
                size="narrow"
                onClick={onUncollapseAndScroll}
                color="devourplay-gradient"
                className={classNames(
                    "go-vip-ow_quest-reward-card_claim-btn",
                    {
                        "mobile-claim-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                Claim
            </FrameButton>
        );
    }

    // Render regular claim button
    return (
        <FrameButton
            size="narrow"
            onClick={() => onClaim(milestoneId || questId)}
            color="devourplay-gradient"
            className={classNames(
                "go-vip-ow_quest-reward-card_claim-btn",
                {
                    "mobile-claim-btn": isMobileView,
                },
                className,
            )}
            forwardProps={{ disabled: isClaimingInProgress }}
        >
            Claim
        </FrameButton>
    );
}